/**
 * Pending-invitation token plumbing.
 *
 * The invitation token is a deep-link credential. It can arrive from
 * the URL (`/auth?invitation=…`) or be carried forward in localStorage
 * across the multi-step auth flow:
 *
 *   - The URL ?invitation= param survives a same-tab navigation but is
 *     stripped by some email-client previews and by the Firebase
 *     email-verify hop (which redirects through a Firebase domain).
 *   - localStorage survives refreshes, cross-tab opens, and the entire
 *     email-verify round-trip.
 *
 * Two carriers means there's a coordination problem — read order,
 * persistence, and clearing all need to agree across Login.tsx,
 * App.tsx AuthRedirect, VerifyEmail.tsx, and auth.service.signUp.
 * This module centralises that.
 *
 * The token carries no owner. Server-side email match stops a wrong-account
 * accept, but a token left behind is still SHOWN to the next person (Login
 * renders the invite) and burned by their refused accept. So a real sign-out
 * clears it (AuthContext's auth listener), as does a user swap on a shared
 * phone (MagicLinkRedemption): CR-049.
 */

const STORAGE_KEY = 'pendingInvitationToken';
const URL_PARAM = 'invitation';

/**
 * Read the current pending-invitation token. URL takes precedence so a
 * fresh deep-link in the address bar wins over a stale localStorage
 * entry from a previous flow.
 */
export function readPendingInvitationToken(): string | null {
  const fromUrl = readFromUrl();
  if (fromUrl) return fromUrl;
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // localStorage can throw in private browsing modes — caller can
    // operate without the carrier; the URL path still works.
    return null;
  }
}

/**
 * Copy any `?invitation=` URL param into localStorage so it survives
 * the Firebase email-verify hop and any cross-tab navigation. Safe to
 * call on every Login mount — no-op when there's nothing in the URL.
 */
export function persistPendingInvitationToken(): string | null {
  const fromUrl = readFromUrl();
  if (!fromUrl) return null;
  try {
    localStorage.setItem(STORAGE_KEY, fromUrl);
  } catch {
    // private browsing — best-effort, the URL still holds the token
  }
  return fromUrl;
}

/**
 * Strip the pending token from BOTH the URL and localStorage. Called
 * after a successful accept (or a definitive reject) so subsequent
 * page loads don't re-attempt the same invitation, and when the user
 * changes (sign-out, a device swap) so the next person never sees it.
 */
export function clearPendingInvitationToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore — best-effort cleanup
  }
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(URL_PARAM)) {
      url.searchParams.delete(URL_PARAM);
      window.history.replaceState({}, document.title, url.toString());
    }
  } catch {
    // ignore — best-effort cleanup
  }
}

/**
 * The workspace an accepted invitation joined, waiting for the app to open
 * it. Accepting and switching happen in different places (the server accepts
 * during Google/Microsoft sign-in; AuthRedirect accepts for a signed-in user)
 * and the effect that switches can be cancelled mid-flight by an auth re-render.
 * This marker is what survives that: `usePendingInvitedWorkspaceSwitch` reads
 * it once the workspace has booted and clears it after switching.
 *
 * Stamped with the accepting user so a shared browser never opens one user's
 * new workspace for the next person to sign in.
 */
const INVITED_WORKSPACE_KEY = 'pendingInvitedWorkspace';

export function recordInvitedWorkspace(workspaceId: string, userId: string): void {
  try {
    localStorage.setItem(INVITED_WORKSPACE_KEY, JSON.stringify({ workspaceId, userId }));
  } catch {
    // private browsing — the in-flow switch still runs; only the retry is lost
  }
}

export function readInvitedWorkspace(userId: string): string | null {
  try {
    const raw = localStorage.getItem(INVITED_WORKSPACE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { workspaceId?: unknown; userId?: unknown };
    if (parsed.userId === userId && typeof parsed.workspaceId === 'string') {
      return parsed.workspaceId;
    }
  } catch {
    // corrupt or unreadable — fall through and drop it
  }
  clearInvitedWorkspace();
  return null;
}

export function clearInvitedWorkspace(): void {
  try {
    localStorage.removeItem(INVITED_WORKSPACE_KEY);
  } catch {
    // ignore — best-effort cleanup
  }
}

/**
 * Why the server refused the invite token sent with a Google/Microsoft sign-in.
 * That path drops its token before posting (the server is the only acceptor),
 * so without this marker a seat-capped or wrong-account invitee hears nothing.
 * `usePendingInvitationRefusalToast` takes it once the app has mounted, shows
 * AuthRedirect's copy, and puts the token back when the refusal is retryable.
 *
 * Stamped with the signing-in user, like the invited-workspace marker. The
 * event wakes a consumer that mounted while the sign-in request was in flight.
 */
const INVITATION_REFUSAL_KEY = 'pendingInvitationRefusal';
export const INVITATION_REFUSAL_EVENT = 'bower:invitation-refusal';

export interface RecordedInvitationRefusal {
  readonly cause: string | null;
  readonly message: string;
  readonly token: string;
}

export function recordInvitationRefusal(refusal: RecordedInvitationRefusal, userId: string): void {
  try {
    localStorage.setItem(INVITATION_REFUSAL_KEY, JSON.stringify({ ...refusal, userId }));
  } catch {
    // private browsing: nothing to show it from
    return;
  }
  window.dispatchEvent(new Event(INVITATION_REFUSAL_EVENT));
}

/** Read and clear: a refusal is shown once. Another user's marker is dropped. */
export function takeInvitationRefusal(userId: string): RecordedInvitationRefusal | null {
  let refusal: RecordedInvitationRefusal | null = null;
  try {
    const raw = localStorage.getItem(INVITATION_REFUSAL_KEY);
    if (!raw) return null;
    localStorage.removeItem(INVITATION_REFUSAL_KEY);
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (
      parsed.userId === userId &&
      typeof parsed.message === 'string' &&
      typeof parsed.token === 'string'
    ) {
      refusal = {
        cause: typeof parsed.cause === 'string' ? parsed.cause : null,
        message: parsed.message,
        token: parsed.token,
      };
    }
  } catch {
    // corrupt or unreadable: nothing to show
  }
  return refusal;
}

/** Put a token back in the carrier so the next accept attempt can retry it. */
export function keepPendingInvitationToken(token: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // private browsing: the invite link itself still carries the token
  }
}

/**
 * Record what `/auth/sso-signin` did with the invite token this client sent:
 * the workspace it joined, or why it was refused.
 */
export function recordSsoInvitationOutcome(
  data: unknown,
  sentToken: string | null,
  userId: string,
): void {
  const { invitedWorkspaceId, invitationRefusal } = (data ?? {}) as {
    invitedWorkspaceId?: unknown;
    invitationRefusal?: { cause?: unknown; message?: unknown } | null;
  };
  if (typeof invitedWorkspaceId === 'string') {
    recordInvitedWorkspace(invitedWorkspaceId, userId);
  }
  if (sentToken && invitationRefusal && typeof invitationRefusal === 'object') {
    recordInvitationRefusal(
      {
        cause: typeof invitationRefusal.cause === 'string' ? invitationRefusal.cause : null,
        message:
          typeof invitationRefusal.message === 'string'
            ? invitationRefusal.message
            : 'Failed to accept invitation',
        token: sentToken,
      },
      userId,
    );
  }
}

function readFromUrl(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get(URL_PARAM);
  } catch {
    return null;
  }
}
